import React from 'react'

/**
 * The game's icon set (#2172), replacing OS-rendered emoji so iconography
 * looks the same on iOS/Android/desktop and doesn't clash with the 2,535
 * hand-made sprite SVGs. Simple geometric shapes at 24x24, matching the
 * sprite set's blocky, flat-shaded language but drawn in a single
 * `currentColor` so each icon themes with whatever text colour surrounds it.
 *
 * Mount <IconSprite /> once near the app root; every <Icon name="..." />
 * instance references a <symbol> here via <use>, so the markup for all 40
 * icons is only ever parsed once regardless of how many places render them.
 */
export const ICON_NAMES = [
  'player', 'deck', 'collection', 'shop', 'codex', 'chronicle', 'news',
  'settings', 'trophy', 'minigames', 'sword', 'infinity', 'hub', 'crystal',
  'lock', 'coin', 'heart', 'mana', 'back-arrow', 'close', 'info', 'filter',
  'search', 'calendar', 'shield', 'bolt', 'pause', 'volume', 'display',
  'database', 'star', 'satchel', 'scroll', 'town', 'warning', 'card', 'pack',
  'timer', 'compass', 'arcade',
] as const

export type IconName = typeof ICON_NAMES[number]

export function IconSprite() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }} aria-hidden="true">
      <defs>
        <symbol id="icon-player" viewBox="0 0 24 24">
          <circle cx="12" cy="7" r="4" />
          <path d="M4 21v-1c0-4.4 3.6-8 8-8s8 3.6 8 8v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
        </symbol>

        <symbol id="icon-deck" viewBox="0 0 24 24">
          <rect x="4" y="8" width="12" height="15" rx="1.5" transform="rotate(-10 10 15.5)" opacity="0.55" />
          <rect x="7" y="3" width="13" height="17" rx="1.5" />
        </symbol>

        <symbol id="icon-collection" viewBox="0 0 24 24">
          <path d="M3 8.5 5 4h14l2 4.5V20a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 20z" />
          <rect x="3" y="8" width="18" height="2" opacity="0.5" />
        </symbol>

        <symbol id="icon-shop" viewBox="0 0 24 24">
          <path d="M8 8Q12 1 16 8" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M5 8h14l-1.4 11.5a1.5 1.5 0 0 1-1.5 1.5H7.9a1.5 1.5 0 0 1-1.5-1.5z" />
        </symbol>

        <symbol id="icon-codex" viewBox="0 0 24 24">
          <path d="M4 4.5A1.5 1.5 0 0 1 5.5 3H19a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H5.5A1.5 1.5 0 0 1 4 19.5z" />
          <path d="M7.2 3.6v16.8" fill="none" stroke="var(--game-bg, #0a0a0a)" strokeWidth="1.7" />
        </symbol>

        <symbol id="icon-chronicle" viewBox="0 0 24 24">
          <rect x="5" y="5" width="14" height="14" rx="1" />
          <ellipse cx="12" cy="5" rx="7" ry="2" />
          <ellipse cx="12" cy="19" rx="7" ry="2" />
        </symbol>

        <symbol id="icon-news" viewBox="0 0 24 24">
          <path d="M4 4h13a1 1 0 0 1 1 1v13.5A1.5 1.5 0 0 0 19.5 20H5a1 1 0 0 1-1-1z" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <rect x="7" y="7.5" width="7" height="4.5" opacity="0.6" />
          <rect x="7" y="14" width="10" height="1.6" opacity="0.6" />
          <rect x="16" y="7.5" width="1.6" height="4.5" opacity="0.6" />
        </symbol>

        <symbol id="icon-settings" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M12 2.5a1.4 1.4 0 0 1 1.37 1.12l.24 1.16a7.6 7.6 0 0 1 1.87.78l1-.63a1.4 1.4 0 0 1 1.75.19l.98.98a1.4 1.4 0 0 1 .19 1.75l-.63 1a7.6 7.6 0 0 1 .78 1.87l1.16.24a1.4 1.4 0 0 1 1.12 1.37v1.4a1.4 1.4 0 0 1-1.12 1.37l-1.16.24a7.6 7.6 0 0 1-.78 1.87l.63 1a1.4 1.4 0 0 1-.19 1.75l-.98.98a1.4 1.4 0 0 1-1.75.19l-1-.63a7.6 7.6 0 0 1-1.87.78l-.24 1.16A1.4 1.4 0 0 1 12 21.5a1.4 1.4 0 0 1-1.37-1.12l-.24-1.16a7.6 7.6 0 0 1-1.87-.78l-1 .63a1.4 1.4 0 0 1-1.75-.19l-.98-.98a1.4 1.4 0 0 1-.19-1.75l.63-1a7.6 7.6 0 0 1-.78-1.87l-1.16-.24A1.4 1.4 0 0 1 2.5 12v-1.4a1.4 1.4 0 0 1 1.12-1.37l1.16-.24a7.6 7.6 0 0 1 .78-1.87l-.63-1a1.4 1.4 0 0 1 .19-1.75l.98-.98a1.4 1.4 0 0 1 1.75-.19l1 .63a7.6 7.6 0 0 1 1.87-.78l.24-1.16A1.4 1.4 0 0 1 12 2.5zm0 6a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z" />
        </symbol>

        <symbol id="icon-trophy" viewBox="0 0 24 24">
          <path d="M7 4h10v6a5 5 0 0 1-10 0z" />
          <path d="M7 5H4a1 1 0 0 0-1 1v1a4 4 0 0 0 4 4M17 5h3a1 1 0 0 1 1 1v1a4 4 0 0 1-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <rect x="10.5" y="15" width="3" height="4" />
          <rect x="7" y="19" width="10" height="2" rx="1" />
        </symbol>

        <symbol id="icon-minigames" viewBox="0 0 24 24">
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <circle cx="9" cy="9" r="1.5" fill="var(--game-bg, #0a0a0a)" />
          <circle cx="15" cy="9" r="1.5" fill="var(--game-bg, #0a0a0a)" />
          <circle cx="9" cy="15" r="1.5" fill="var(--game-bg, #0a0a0a)" />
          <circle cx="15" cy="15" r="1.5" fill="var(--game-bg, #0a0a0a)" />
          <circle cx="12" cy="12" r="1.5" fill="var(--game-bg, #0a0a0a)" />
        </symbol>

        <symbol id="icon-sword" viewBox="0 0 24 24">
          <rect x="10.8" y="2" width="2.4" height="12" rx="1" />
          <rect x="7.5" y="13" width="9" height="2.2" rx="1" />
          <rect x="10.4" y="15" width="3.2" height="5" rx="1.2" />
          <circle cx="12" cy="21" r="1.4" />
        </symbol>

        <symbol id="icon-shield" viewBox="0 0 24 24">
          <path d="M12 1L3 5v6c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12V5l-9-4z" />
        </symbol>

        <symbol id="icon-bolt" viewBox="0 0 24 24">
          <path d="M13 2L4 14h6l-1 8 9-12h-6z" />
        </symbol>

        <symbol id="icon-pause" viewBox="0 0 24 24">
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </symbol>

        <symbol id="icon-infinity" viewBox="0 0 24 24">
          <path fillRule="evenodd" d="M7 8a4.5 4.5 0 0 0 0 9c1.9 0 3.1-1.1 4-2.3l1-1.4 1 1.4c.9 1.2 2.1 2.3 4 2.3a4.5 4.5 0 0 0 0-9c-1.9 0-3.1 1.1-4 2.3l-1 1.4-1-1.4C10.1 9.1 8.9 8 7 8zm0 2.6c.83 0 1.5.5 2.2 1.5l.5.7-.5.7c-.7 1-1.37 1.5-2.2 1.5a2.1 2.1 0 0 1 0-4.4zm10 0a2.1 2.1 0 0 1 0 4.4c-.83 0-1.5-.5-2.2-1.5l-.5-.7.5-.7c.7-1 1.37-1.5 2.2-1.5z" />
        </symbol>

        <symbol id="icon-hub" viewBox="0 0 24 24">
          <path d="M12 3 3 10.5V21h6v-6h6v6h6V10.5z" />
        </symbol>

        <symbol id="icon-crystal" viewBox="0 0 24 24">
          <path d="M12 2 5 8l7 14 7-14z" />
          <path d="M5 8h14L12 2z" opacity="0.6" />
        </symbol>

        <symbol id="icon-lock" viewBox="0 0 24 24">
          <path d="M7 10V7.5a5 5 0 0 1 10 0V10" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <rect x="4.5" y="10" width="15" height="11" rx="2" />
          <rect x="11" y="14" width="2" height="4" rx="1" fill="var(--game-bg, #0a0a0a)" />
        </symbol>

        <symbol id="icon-coin" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="6.2" fill="none" stroke="var(--game-bg, #0a0a0a)" strokeWidth="1.4" />
        </symbol>

        <symbol id="icon-heart" viewBox="0 0 24 24">
          <path d="M12 20.5S3 14.8 3 8.8A4.8 4.8 0 0 1 12 6a4.8 4.8 0 0 1 9 2.8c0 6-9 11.7-9 11.7z" />
        </symbol>

        <symbol id="icon-mana" viewBox="0 0 24 24">
          <path d="M12 2S5 12 5 16a7 7 0 0 0 14 0c0-4-7-14-7-14z" />
        </symbol>

        <symbol id="icon-back-arrow" viewBox="0 0 24 24">
          <path d="M11 4 4 12l7 8" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4 12h16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
        </symbol>

        <symbol id="icon-close" viewBox="0 0 24 24">
          <rect x="11" y="2" width="2" height="20" rx="1" transform="rotate(45 12 12)" />
          <rect x="11" y="2" width="2" height="20" rx="1" transform="rotate(-45 12 12)" />
        </symbol>

        <symbol id="icon-info" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9.5" />
          <rect x="10.8" y="10.5" width="2.4" height="7" rx="1" fill="var(--game-bg, #0a0a0a)" />
          <circle cx="12" cy="7.2" r="1.5" fill="var(--game-bg, #0a0a0a)" />
        </symbol>

        <symbol id="icon-filter" viewBox="0 0 24 24">
          <path d="M3 4h18l-6.5 8v6l-5 3v-9z" />
        </symbol>

        <symbol id="icon-search" viewBox="0 0 24 24">
          <circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.4" />
          <rect x="17.5" y="15.5" width="2.6" height="7" rx="1.3" transform="rotate(-45 18.8 19)" />
        </symbol>

        <symbol id="icon-calendar" viewBox="0 0 24 24">
          <rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <path d="M3 9.5h18" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <rect x="6.8" y="2" width="2.4" height="5" rx="1.2" />
          <rect x="14.8" y="2" width="2.4" height="5" rx="1.2" />
        </symbol>

        <symbol id="icon-volume" viewBox="0 0 24 24">
          <path d="M4 9h3.5L13 4.5v15L7.5 15H4a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1z" />
          <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </symbol>

        <symbol id="icon-arcade" viewBox="0 0 24 24">
          <rect x="3" y="15" width="18" height="6" rx="1.5" />
          <rect x="11" y="7" width="2" height="9" />
          <circle cx="12" cy="6" r="3.5" />
          <circle cx="17.5" cy="13" r="1.5" />
        </symbol>

        <symbol id="icon-display" viewBox="0 0 24 24">
          <rect x="2" y="4" width="20" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <path d="M8 21h8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M12 17v4" fill="none" stroke="currentColor" strokeWidth="2.2" />
        </symbol>

        <symbol id="icon-database" viewBox="0 0 24 24">
          <ellipse cx="12" cy="6" rx="8" ry="3.2" />
          <path d="M4 6v5c0 1.77 3.58 3.2 8 3.2s8-1.43 8-3.2V6M4 11v5c0 1.77 3.58 3.2 8 3.2s8-1.43 8-3.2v-5" fill="none" stroke="currentColor" strokeWidth="2.2" />
        </symbol>

        <symbol id="icon-star" viewBox="0 0 24 24">
          <path d="M12 1.8l2.5 6.2 6.7.4-5.2 4.3 1.7 6.5L12 15.6l-5.7 3.6 1.7-6.5-5.2-4.3 6.7-.4z" />
        </symbol>

        {/* A messenger bag: trapezoid flap over a wide body. Deliberately no
            arc handle — an arc above a filled body reads as a padlock
            shackle at nav size, colliding with icon-lock. */}
        <symbol id="icon-satchel" viewBox="0 0 24 24">
          <path d="M6 3h12l3.2 6.5H2.8z" />
          <path d="M2.5 9.5h19a1 1 0 0 1 1 1v8a2 2 0 0 1-2 2H3.5a2 2 0 0 1-2-2v-8a1 1 0 0 1 1-1z" />
          <path d="M2.2 13.6h19.6" fill="none" stroke="var(--game-bg, #0a0a0a)" strokeWidth="1.8" />
          <rect x="10.6" y="12.2" width="2.8" height="3.2" rx="0.8" />
        </symbol>

        <symbol id="icon-scroll" viewBox="0 0 24 24">
          <path d="M6 3h11a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
          <path d="M8 8h7M8 12h7M8 16h4" fill="none" stroke="var(--game-bg, #0a0a0a)" strokeWidth="1.8" strokeLinecap="round" />
        </symbol>

        <symbol id="icon-town" viewBox="0 0 24 24">
          <path d="M1.5 21v-7l5-4 5 4v7z" />
          <path d="M13 21v-8.5l4.5-3.5 4.5 3.5V21z" />
          <rect x="5" y="16.5" width="3" height="4.5" fill="var(--game-bg, #0a0a0a)" />
          <rect x="16" y="16.5" width="3" height="4.5" fill="var(--game-bg, #0a0a0a)" />
        </symbol>

        {/* Same exclamation-mark construction as icon-info (stem + dot cut
            from the fill) but on a triangle rather than a circle, so the two
            read as a matched pair — "for your information" vs. "heads up". */}
        <symbol id="icon-warning" viewBox="0 0 24 24">
          <path d="M12 2 22.5 21H1.5z" />
          <rect x="10.8" y="8.5" width="2.4" height="6.3" rx="1" fill="var(--game-bg, #0a0a0a)" />
          <circle cx="12" cy="17.7" r="1.4" fill="var(--game-bg, #0a0a0a)" />
        </symbol>

        {/* One upright card with a diamond pip — "you got a card", distinct
            from icon-deck's two overlapping cards ("your whole deck"). */}
        <symbol id="icon-card" viewBox="0 0 24 24">
          <rect x="5" y="2" width="14" height="20" rx="2" />
          <path d="M12 8l2.6 4.5L12 17l-2.6-4.5z" fill="var(--game-bg, #0a0a0a)" />
        </symbol>

        {/* A wrapped gift box — the card-pack purchase, distinct from
            icon-collection's storage-box shape. Ribbon cross cut from the
            fill; the bow is two circles plus a small knot rather than a
            freehand loop, so it stays legible at 24x24. */}
        <symbol id="icon-pack" viewBox="0 0 24 24">
          <rect x="4" y="8" width="16" height="13" rx="2" />
          <rect x="10.5" y="8" width="3" height="13" fill="var(--game-bg, #0a0a0a)" />
          <rect x="4" y="12.5" width="16" height="3" fill="var(--game-bg, #0a0a0a)" />
          <circle cx="9" cy="5.3" r="2.2" />
          <circle cx="15" cy="5.3" r="2.2" />
          <rect x="10.9" y="4" width="2.2" height="2.6" rx="0.6" />
        </symbol>

        {/* A clock face — hands cut from the fill, same construction as
            icon-info's stem-and-dot — for any countdown or time-remaining
            indicator (a locked hero card's cooldown, a shop restock, a
            playtime stat). */}
        <symbol id="icon-timer" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <rect x="11" y="5" width="2" height="8" rx="1" fill="var(--game-bg, #0a0a0a)" />
          <rect x="12" y="11" width="6" height="2" rx="1" fill="var(--game-bg, #0a0a0a)" />
        </symbol>

        {/* A compass — an outlined ring (same open-circle technique as
            icon-search) with a kite-shaped needle pointing off-center — for
            a trader role that isn't tied to one shop. */}
        <symbol id="icon-compass" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M15.5 8.5 13.5 14 8.5 15.5 10.5 10z" />
        </symbol>
      </defs>
    </svg>
  )
}
